import { PageController } from '@client/components/_core/layout/PageController';
import { VDropdown } from '@client/components/_shared/forms/view-form/VDropdown';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { PaymentAccountDetails } from '@client/components/claims/financials/payment-account/PaymentAccountDetails';
import { ContactList } from '@client/components/contacts/ContactList';
import { ProductContactsPanel } from '@client/components/contacts/ProductContactsPanel';
import { SupplierContactList } from '@client/components/contacts/SupplierContactList';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { type Claim, claimFormStore } from '../store';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/contacts')({
    component: Contacts,
});

function ProductContacts({ claim, isReadOnly }: { claim: Claim; isReadOnly: boolean }) {
    const { data, isPending } = trpc.productContact.listContactTypesForClaim.useQuery({ claimId: claim.id });

    if (isPending) {
        return <Loading />;
    }

    return <ProductContactsPanel claim={claim} contactTypes={data?.contactTypes ?? []} isReadOnly={isReadOnly} />;
}

function Contacts() {
    const orgId = useOrgId();
    const claim = claimFormStore.use.formData();
    const isReadonly = claimFormStore.use.isReadonly();
    const { trpcUtils } = useTrpc();
    const navigate = useNavigate();

    /**
     * Two rendering modes, separated by data that does not exist on a legacy claim:
     * a claim lodged under a product version renders sections from its configuration,
     * everything else renders exactly as it always has. The configuration is fetched
     * only on this branch, so a legacy claim pays nothing for it.
     */
    if (claim.productVersionId) {
        return (
            <PageController route={Route}>
                <Outlet />
                <ProductContacts claim={claim} isReadOnly={isReadonly} />
            </PageController>
        );
    }

    return (
        <PageController route={Route}>
            <Outlet />
            <ContactList
                type="POLICY_HOLDER"
                entity="claim"
                entityId={claim.id}
                title="Policy holder"
                isReadOnly={isReadonly}
                isCollapsible
                isDefaultExpanded
                titleRightContent={
                    <VTextInput
                        label="Company"
                        field="phCompany"
                        formStore={claimFormStore}
                        rules={[{ required: true }]}
                        layout="horizontal"
                        isReadOnly={isReadonly}
                        buttonsPosition="right"
                    />
                }
                bottomAddon={
                    <div className="border-separator border-t px-6 pt-3 pb-7">
                        <PaymentAccountDetails
                            entity="POLICY_HOLDER"
                            entityId={claim.id}
                            isReadonly={isReadonly}
                            className="mt-4"
                        />
                    </div>
                }
                onRowClick={({ id }, e) =>
                    navigate(
                        {
                            to: '/console/$orgId/claims/details/$id/contacts/edit/$contactId',
                            params: {
                                orgId,
                                id: claim.id,
                                contactId: id,
                            },
                            search: {
                                type: 'POLICY_HOLDER',
                            },
                        },
                        e,
                    )
                }
            />
            <ContactList
                type="BROKER"
                entity="claim"
                entityId={claim.id}
                title="Broker"
                isReadOnly={isReadonly}
                isCollapsible
                titleRightContent={
                    <VTextInput
                        label="Company"
                        field="brokerCompany"
                        formStore={claimFormStore}
                        rules={[{ required: true }]}
                        layout="horizontal"
                        isReadOnly={isReadonly}
                        buttonsPosition="right"
                    />
                }
                onRowClick={({ id }, e) =>
                    navigate(
                        {
                            to: '/console/$orgId/claims/details/$id/contacts/edit/$contactId',
                            params: {
                                orgId,
                                id: claim.id,
                                contactId: id,
                            },
                            search: {
                                type: 'BROKER',
                            },
                        },
                        e,
                    )
                }
            />
            <ContactList
                type="INSURER"
                entity="claim"
                entityId={claim.id}
                title="Insurer"
                isReadOnly={isReadonly}
                isCollapsible
                titleRightContent={
                    <VDropdown
                        label="Company"
                        field="insurerId"
                        formStore={claimFormStore}
                        layout="horizontal"
                        isReadOnly={isReadonly}
                        formatValueFn={(_, { Insurer }) => Insurer?.name}
                        loaderFn={async () => {
                            if (!claim.productTypeId) {
                                return [];
                            }

                            const data = await trpcUtils.insurer.listInsurersForSelect.fetch({
                                where: {
                                    productTypeId: claim.productTypeId,
                                },
                                orderBy: {
                                    name: 'asc',
                                },
                            });

                            return data.rows.map(({ id, name }) => ({
                                value: id,
                                label: name,
                            }));
                        }}
                    />
                }
                onRowClick={({ id }, e) =>
                    navigate(
                        {
                            to: '/console/$orgId/claims/details/$id/contacts/edit/$contactId',
                            params: {
                                orgId,
                                id: claim.id,
                                contactId: id,
                            },
                            search: {
                                type: 'INSURER',
                            },
                        },
                        e,
                    )
                }
            />
            <SupplierContactList
                supplierIds={[...new Set(claim.ClaimSuppliers.map(({ supplierId }) => supplierId))]}
                isReadonly={isReadonly}
            />
            <ContactList
                type="OTHER"
                entity="claim"
                entityId={claim.id}
                title="Third party"
                isReadOnly={isReadonly}
                isCollapsible
                expandable={{
                    expandedRowRender(item) {
                        return (
                            <PaymentAccountDetails entity="OTHER_CONTACT" entityId={item.id} isReadonly={isReadonly} />
                        );
                    },
                }}
                onRowClick={({ id, type }, e) =>
                    navigate(
                        {
                            to: '/console/$orgId/claims/details/$id/contacts/edit/$contactId',
                            params: {
                                orgId,
                                id: claim.id,
                                contactId: id,
                            },
                            search: {
                                type,
                            },
                        },
                        e,
                    )
                }
            />
        </PageController>
    );
}
